<template>
  <div
    class="rounded-lg dark:bg-black min-h-screen bg-gray-100 p-6 md:p-10 w-full h-full flex flex-col justify-center items-center shadow-lg space-y-6"
  >
    <div
      v-if="loading"
      class="bg flex flex-col justify-center items-center h-full w-full"
    >
      <div class="animate-spin"><Settings :size="40" /></div>

      <div class="text-lg font-semibold font-mono">Connecting your account</div>
    </div>
    <div v-else>
      <div class="flex flex-col items-center justify-center mb-6">
        <img src="/images/amazon.png" alt="Amazon Ads  Logo" class="h-8 mr-3" />
        <div
          v-if="connected"
          class="font-medium text-gray-800 dark:text-gray-100 text-center lg:text-lg text-base h-full w-full p-4"
        >
          Connected Successfully . Navigating you back .....
        </div>
        <div class="text-2xl font-bold text-gray-800 dark:text-gray-100" v-else>
          Choose Profiles to Sync Data From
        </div>
      </div>
      <div v-if="!connected">
        <!-- Instruction Text -->
        <p class="text-gray-600 dark:text-gray-300 text-center mb-4">
          Please enter one or more Profile Id's from the list below to sync with
          Amazon Ads. You can add and remove profile id's, then click "Connect
          Selected Profiles" to proceed.
        </p>

        <!-- Input Fields for profile IDs -->
        <div
          v-for="(profileId, index) in profileIds"
          :key="index"
          class="flex items-center mb-4 space-x-3"
        >
          <input
            v-model="profileIds[index]"
            type="text"
            placeholder="Enter Profile ID"
            class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-white dark:border-gray-600"
          />
          <button
            @click="removeProfileId(index)"
            class="text-red-500 hover:text-red-700 font-semibold transition duration-200 ease-in-out"
          >
            <CircleMinus />
          </button>
        </div>

        <div class="flex justify-center mb-4">
          <button
            @click="addNewProfileId"
            class="text-blue-600 font-semibold hover:underline transition duration-200 ease-in-out flex gap-2"
          >
            <CirclePlus /> Add Profile Id
          </button>
        </div>

        <!-- Connect Button -->
        <div class="flex justify-end mt-8">
          <button
            v-if="secretId"
            @click="establishConnection"
            :disabled="profileIds.length == 0"
            class="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
          >
            Connect Selected Profiles
          </button>
          <button
            v-else
            @click="continueConection"
            :disabled="profileIds.length == 0"
            class="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
          >
            Continue Selecting Profile ID's
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Api from '@/utils/ApiClient';
import { ID_VALIDATION_ERROR } from '@/utils/constantData';
import { processId, validateID } from '@/utils/helperFunctions';
import { CircleMinus, CirclePlus, Settings } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';

const secretId = ref();
const sourceType = 'amazon-ads';
const route = useRoute();
const router = useRouter();
const loading = ref(false);
const profileIds = ref([]);
const connected = ref(false);

const addNewProfileId = () => {
  profileIds.value.push('');
};

const removeProfileId = (index) => {
  profileIds.value.splice(index, 1);
};
const validateProfileIds = () => {
  return profileIds.value.every((id) => {
    const idString = processId(id).toString();
    return validateID(idString);
  });
};
const establishConnection = async () => {
  try {
    loading.value = true;
    if (!validateProfileIds()) {
      toast.error(ID_VALIDATION_ERROR);
      return;
    }
    const payloadProfiles = profileIds.value.map((id) => id.toString());
    const response = await Api.post(
      `/airbyte/amazon-ads`,
      {
        profile_id: payloadProfiles,
        secret_id: secretId.value,
      },
      {
        params: {
          source_type: sourceType,
        },
      },
    );

    if (response.data.message) {
      toast.success(response.data.message || 'Account Connected successfully', {
        onClose: () => {
          router.push('/settings/team/connections');
        },
      });
      connected.value = true;
    }
  } catch (error) {
    toast.error(error.response.data.detail || 'Account Connection Failed!');
  } finally {
    loading.value = false;
  }
};
const continueConection = async () => {
  try {
    loading.value = true;
    if (!validateProfileIds()) {
      toast.error(ID_VALIDATION_ERROR);
      return;
    }
    const payloadProfiles = profileIds.value.map((id) => id.toString());
    const response = await Api.patch(
      `/airbyte/amazon-ads`,
      {
        profile_id: payloadProfiles,
      },
      {
        params: {
          source_type: sourceType,
        },
      },
    );

    if (response.data.message) {
      toast.success(response.data.message || 'Account Connected successfully', {
        onClose: () => {
          router.push('/settings/team/connections');
        },
      });
      connected.value = true;
    }
  } catch (error) {
    toast.error(error.response.data.detail || 'Something went wrong');
  } finally {
    loading.value = false;
  }
};
onMounted(() => {
  if (route.query.secret_id) {
    toast.success('Account Authorized successfully!');
    secretId.value = route.query.secret_id;
  }
});
</script>
